import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { map, Observable, take } from 'rxjs';
import {
  PictureAutomobilePlatform,
  PictureAutomobilePlatformDetail,
} from '../domain/PictureAutomobilePlatform.model';
import { ResponseApi } from '@domain/shared/infrastructure/models/response-api.interface';
import {
  SetMapPictureAutomobilePlatform,
  SetMapPictureAutomobilePlatforms,
} from '../mappers/picture-automobile-platform.mappers';
import {
  PictureAutomobilePlatformApi,
  PictureAutomobilePlatformDetailApi,
} from './models/picture-automobile-platform-api.model';
import { PictureAutomobilePlatformCreate } from '../domain/PictureAutomobilePlatformCreate.model';
import { SetMapPictureAutomobilePlatformDetails } from '../mappers/picture-automobile-platform-details.mapper';
import { PictureAutomobilePlatformUpdate } from '../domain/PictureAutomobilePlatformUpdate.model';

@Injectable({
  providedIn: 'root',
})
export class PictureAutomobilePlatformApiService {
  private readonly BASE_URL = environment.baseUrl;
  private _http = inject(HttpClient);

  getAllPictureAutomobilePlatform(): Observable<PictureAutomobilePlatform[]> {
    const url = `${this.BASE_URL}picture-automobile-platform`;
    return this._http.get<ResponseApi>(url).pipe(
      take(1),
      map((response) => {
        if (!response.ok) return [];
        return SetMapPictureAutomobilePlatforms(
          response.results as PictureAutomobilePlatformApi[]
        );
      })
    );
  }

  getAllPictureAutombilePlatformDetailByActivity(
    activityId: number,
    isLoad: boolean
  ): Observable<PictureAutomobilePlatformDetail[]> {
    const url = `${this.BASE_URL}picture-automobile-platform/activity/${activityId}/load/${isLoad}`;
    return this._http.get<ResponseApi>(url).pipe(
      take(1),
      map((response) => {
        if (!response.ok || !response.results) return [];
        return SetMapPictureAutomobilePlatformDetails(
          response.results as PictureAutomobilePlatformDetailApi[]
        );
      })
    );
  }

  getPictureAutomobilePlatformById(
    id: number
  ): Observable<PictureAutomobilePlatform | null> {
    const url = `${this.BASE_URL}picture-automobile-platform/${id}`;
    return this._http.get<ResponseApi>(url).pipe(
      take(1),
      map((response) => {
        if (!response.ok) return null;
        return SetMapPictureAutomobilePlatform(
          response.results as PictureAutomobilePlatformApi
        );
      })
    );
  }

  getPictureAutomobilePlatformByAutomobileId(
    automobileID: number
  ): Observable<string[] | null> {
    const url = `${this.BASE_URL}picture-automobile-platform/activity/${automobileID}`;
    return this._http.get<ResponseApi>(url).pipe(
      take(1),
      map((response) => {
        if (!response.ok) return null;
        return response.results as string[];
      })
    );
  }

  createPictureAutomobilePlatform(
    createPictureAutomobile: PictureAutomobilePlatformCreate
  ): Observable<PictureAutomobilePlatform | null> {
    const url = `${this.BASE_URL}picture-automobile-platform`;
    const headers = new HttpHeaders();
    return this._http
      .post<ResponseApi>(url, createPictureAutomobile, { headers })
      .pipe(
        take(1),
        map((response) => {
          if (!response.ok) return null;
          return SetMapPictureAutomobilePlatform(
            response.results as PictureAutomobilePlatformApi
          );
        })
      );
  }

  updatePictureAutomobilePlatform(
    picturesAutomobileUpdates: PictureAutomobilePlatformUpdate[]
  ): Observable<PictureAutomobilePlatform[]> {
    const url = `${this.BASE_URL}picture-automobile-platform`;
    const headers = new HttpHeaders();
    return this._http
      .put<ResponseApi>(url, picturesAutomobileUpdates, { headers })
      .pipe(
        take(1),
        map((response) => {
          if (!response.ok) return [];
          return SetMapPictureAutomobilePlatforms(
            response.results as PictureAutomobilePlatformApi[]
          );
        })
      );
  }

  deletePictureAutomobilePlatform(id: number): Observable<boolean> {
    const url = `${this.BASE_URL}picture-automobile-platform/${id}`;
    const headers = new HttpHeaders();
    return this._http.delete<ResponseApi>(url, { headers }).pipe(
      take(1),
      map((response) => {
        return response.ok;
      })
    );
  }
}
